import { MdDelete, MdDownload, MdPrint, MdRemoveRedEye } from "react-icons/md";
import { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { getDocLink, resetViewConfig } from "../../../utils/helpers";
import Modal from "../../../components/Modal";
import CustomLoader from "../../../components/CustomLoader";
import PdfPrintViewer, { ModalInner, PdfContainer } from "./PdfPrintViewer";
import CSVViewer from "./CsvViewer";
import DocxViewer from "./DocxViewer";
import moment from "moment";

interface AttachmentLineItemProps {
  attachment: {
    attachmentId: string;
    name: string;
    uploadedBy: string;
    uploadedAt: string;
    fileExtension: string;
    data?: string;
  };
  setAttachmentId: (id: string) => void;
  setFileViewConfig: (config: any) => void;
  fileViewConfig: any;
  setDeleteConfig: (config: any) => void;
  handleDeleteMethod?: (id: string) => void;
  showDelete: boolean;
}

export const AttachmentLineItem = ({
  attachment,
  setAttachmentId,
  setFileViewConfig,
  fileViewConfig,
  setDeleteConfig,
  handleDeleteMethod,
  showDelete,
}: AttachmentLineItemProps) => {
  return (
    <li
      key={attachment?.attachmentId}
      className="mb-4 block items-center rounded-lg border-2 border-gray-200 p-5 py-2 shadow"
    >
      <span className="block text-base text-gray-600">{attachment.name}</span>
      <span className="mr-4 text-base text-gray-500">
        Uploaded by: {attachment?.uploadedBy}
      </span>
      <span style={{ display: "flex", gap: "10px", alignItems: "center" }}>
        {/* View icon - show for viewable file types */}
        {["pdf", "png", "jpg", "jpeg", "csv", "docx"].includes(attachment.fileExtension) && (
          <MdRemoveRedEye
            style={{ cursor: "pointer" }}
            size={20}
            onClick={() => {
              setAttachmentId(attachment.attachmentId);
              setFileViewConfig({
                ...fileViewConfig,
                viewFile: true,
                fileExtension: attachment.fileExtension,
              });
            }}
          />
        )}
        {/* Download icon - hide only when data is explicitly empty (still loading in QAPI context) */}
        {attachment.data !== '' && ["pdf", "csv", "docx", "png", "jpg", "jpeg"].includes(attachment.fileExtension) && (
          <a
            style={{ color: "initial", display: "flex", alignItems: "center" }}
            href={getDocLink(attachment)}
            download={`${attachment.name}.${attachment.fileExtension}`}
          >
            <MdDownload style={{ cursor: "pointer" }} size={20} />
          </a>
        )}
        {showDelete && (
          <MdDelete
            style={{ cursor: "pointer" }}
            size={20}
            onClick={() => {
              setDeleteConfig({
                showDelete: true,
                id: "",
                fn: () => {
                  if (handleDeleteMethod)
                    handleDeleteMethod(attachment.attachmentId);
                  else return;
                },
              });
            }}
          />
        )}
      </span>
      <span className="mr-4 block text-base text-gray-500">
        Upload date: {attachment?.uploadedAt}
      </span>
    </li>
  );
};

interface FileViewConfig {
  viewFile: boolean;
  file: string;
  fileExtension: string;
}

interface FileViewModalProps {
  fileViewConfig: FileViewConfig;
  setFileViewConfig: (
    config: FileViewConfig | ((prev: FileViewConfig) => FileViewConfig)
  ) => void;
  isLoadingAttachment: boolean;
  setAttachmentId: (id: string) => void;
}

export const FileViewModal = ({
  fileViewConfig,
  setFileViewConfig,
  isLoadingAttachment,
  setAttachmentId,
}: FileViewModalProps) => {
  const printRef = useRef<HTMLDivElement>(null);
  const printRendered = useReactToPrint({
    content: () => printRef.current,
    documentTitle: "attachment",
  });
  const handlePrint = async () => {
    if (fileViewConfig.fileExtension === "pdf") {
      // Browsers render PDFs natively in a new tab. Use a Blob URL (data URLs are
      // unreliable for large PDFs) and give the native viewer time to initialize
      // before triggering print, otherwise the print dialog opens against a blank page.
      try {
        const res = await fetch(fileViewConfig.file);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const win = window.open(url, "_blank");
        if (win) {
          setTimeout(() => {
            try { win.print(); } catch { /* user can still use the native viewer's print button */ }
          }, 1200);
        }
      } catch {
        window.open(fileViewConfig.file, "_blank");
      }
      return;
    }
    printRendered();
  };
  const canPrint = ["pdf", "png", "jpg", "jpeg", "docx"].includes(
    fileViewConfig.fileExtension
  );
  return (
    <Modal
      open={fileViewConfig.viewFile}
      close={() => setFileViewConfig(resetViewConfig())}
    >
      {isLoadingAttachment ? (
        <CustomLoader />
      ) : (
        <ModalInner>
          <button
            onClick={() => {
              setFileViewConfig(resetViewConfig());
              setAttachmentId("");
            }}
            title="Close"
            style={{ 
              position: "fixed", 
              right: "20px", 
              top: "20px",
              zIndex: "9999",
              width: "50px",
              height: "50px",
              backgroundColor: "#482952",
              color: "#ffffff",
              border: "2px solid #ffffff",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "40px",
              fontWeight: "bold",
              lineHeight: "1",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
              cursor: "pointer",
              transition: "all 0.2s",
              fontFamily: "Arial, sans-serif",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#5e3469";
              e.currentTarget.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#482952";
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            ×
          </button>
          {canPrint && (
            <button
              onClick={handlePrint}
              title="Print"
              style={{
                position: "fixed",
                right: "150px",
                top: "20px",
                zIndex: "9999",
                width: "50px",
                height: "50px",
                backgroundColor: "#482952",
                color: "#ffffff",
                border: "2px solid #ffffff",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
                cursor: "pointer",
              }}
            >
              <MdPrint size={24} />
            </button>
          )}
          {fileViewConfig.file && (
            <a
              href={fileViewConfig.file}
              download={`attachment.${fileViewConfig.fileExtension}`}
              title="Download"
              style={{
                position: "fixed",
                right: "85px",
                top: "20px",
                zIndex: 9999,
                width: "50px",
                height: "50px",
                backgroundColor: "#482952",
                color: "#ffffff",
                border: "2px solid #ffffff",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
                cursor: "pointer",
                textDecoration: "none",
              }}
            >
              <MdDownload size={24} />
            </a>
          )}
          <div ref={printRef} style={{ width: "100%" }}>
          {["png", "jpg", "jpeg"].includes(fileViewConfig.fileExtension) && (
            <div
              style={{
                height: "18rem",
                width: "18rem",
                paddingTop: "2rem",
              }}
            >
              <img
                style={{
                  height: "100%",
                  width: "100%",
                  objectFit: "contain",
                }}
                src={fileViewConfig.file}
                alt="avatar"
              />
            </div>
          )}
          {fileViewConfig.fileExtension === "pdf" && (
            <PdfContainer>
              <PdfPrintViewer fileSrc={fileViewConfig.file} fullscreen />
            </PdfContainer>
          )}
          {fileViewConfig.fileExtension === "csv" && (
            <CSVViewer base64String={fileViewConfig.file} />
          )}
          {fileViewConfig.fileExtension === "docx" && (
            <DocxViewer fileSrc={fileViewConfig.file} />
          )}
          </div>
        </ModalInner>
      )}
    </Modal>
  );
};

export const SelectedFilesView = ({
  selectedFiles,
  setDeleteConfig,
  handleFileDelete,
  single = false,
  isTooLarge = false,
  fileSize,
}: any) => {
  return (
    <div className="mt-4">
      <ul className="p-0">
        {fileSize / 1000000}
        {!single && (
          <p className="mb-3 block text-red-600">
            Leaving this page will remove items not uploaded yet. Click on
            Resubmit to upload the selected file(s)
          </p>
        )}
        {isTooLarge && (
          <p className="mb-3 block animate-pulse text-red-600">
            The Total file size is {+(fileSize / 1000000).toFixed(2)} MB which
            exceeds 4 MB. Please upload in batches or compress if possible.
          </p>
        )}
        {selectedFiles?.map((file: any, index: any) => (
          <li
            key={`${file.name}${index}`}
            className="mb-4 block min-w-0 items-center rounded-lg border-2 border-gray-200 p-5 py-2 shadow"
            style={{ textAlign: "left" }}
          >
            <span className="block truncate text-gray-600">{file.name}</span>
            <span className="block text-xs text-gray-500">
              {file?.data?.size / 1000} KB
            </span>
            <span className="text-xs text-gray-500">
              {moment().format("MM/DD/YYYY HH:mm")}
            </span>
            <span>
              <MdDelete
                style={{ cursor: "pointer" }}
                size={15}
                onClick={() => {
                  !single
                    ? setDeleteConfig({
                        showDelete: true,
                        id: index,
                        fn: () => handleFileDelete(index),
                      })
                    : handleFileDelete(index);
                }}
              />
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};
